// "Make food" — the production-batch tab of the Kitchen Manager's Production
// page (kitchen flow lane B, wired into lane C's screen). Pick what you are
// making, see what it takes from the kitchen, start it, then say how much it
// actually made. Every action goes through the page's offline queue (`run`),
// so a dropped connection saves the tap on the tablet instead of losing it;
// the server treats a replayed key as a no-op, and finishing twice moves stock
// once. Costs are shown (Stage 2D — the kitchen manager sees costs).
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChefHat, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { T } from '@/lib/inventoryFlow/strings'
import { formatMoney } from '@/lib/currency'
import type { KitchenPrepItem, ProductionBatch } from '@/types/kitchenFlowPrep'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { fmtQty } from '@/lib/quantity'
import { cn, formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { batchPlanLabel } from '@/lib/inventoryFlow/planLogic'
import { kitchenIngredientsFor, yieldVarianceText } from '@/components/store/inventory/prep/prepRecipeDraft'
import { PREP_TEXT } from '@/components/store/inventory/prep/prepStrings'
import { businessTodayIso, defaultExpiryFor, expiryMissing } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import type { KitchenRun } from './RequisitionPanel'
import { LoadError } from './LoadError'
import { PrepRecipeButton } from '@/components/recipe-card/RecipeCardHost'

type Notify = (kind: 'success' | 'error', message: string) => void

const QK = ['inventory-flow', 'production'] as const

// Batch 2: a batch that finished short keeps its warning on the card (it
// used to be a red toast that vanished, though the batch had saved). Kept on
// this tablet until someone taps OK.
const SHORT_KEY = 'kf.batchShortfalls'

function readShortfalls(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(SHORT_KEY)
    return raw ? (JSON.parse(raw) as Record<string, string>) : {}
  } catch {
    return {}
  }
}

function writeShortfalls(v: Record<string, string>) {
  try {
    window.localStorage.setItem(SHORT_KEY, JSON.stringify(v))
  } catch {
    // storage blocked: the warning lasts while the screen is open
  }
}

function PlanFor({ batch }: { batch: ProductionBatch }) {
  const label = batchPlanLabel(batch, formatDateHuman)
  return label ? <p className="text-sm font-medium">{PREP_TEXT.forPlan(label)}</p> : null
}

const STATUS_TEXT: Record<ProductionBatch['status'], string> = {
  planned: PREP_TEXT.statusPlanned,
  in_progress: PREP_TEXT.statusInProgress,
  done: PREP_TEXT.statusDone,
  cancelled: PREP_TEXT.statusCancelled,
}

export function MakeFoodTab({ run, notify }: { run: KitchenRun; notify: Notify }) {
  const { data: items = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [...QK, 'prep-items'],
    queryFn: () => apiClient.listKitchenPrepItems().then((r) => r.data ?? []),
  })
  const { data: open = [] } = useQuery({
    queryKey: [...QK, 'open'],
    queryFn: () => apiClient.listProductionBatches({ status: ['planned', 'in_progress'] }).then((r) => r.data ?? []),
  })
  const { data: recent = [] } = useQuery({
    queryKey: [...QK, 'recent'],
    queryFn: () => apiClient.listProductionBatches({ status: ['done'], limit: 8 }).then((r) => r.data ?? []),
  })
  const [pickedId, setPickedId] = useState<string | null>(null)
  const picked = items.find((i) => i.stock_item_id === pickedId)
  const [shortfalls, setShortfalls] = useState<Record<string, string>>(readShortfalls)
  const setShortfall = useCallback((batchId: string, text: string | null) => {
    setShortfalls((cur) => {
      const next = { ...cur }
      if (text) next[batchId] = text
      else delete next[batchId]
      writeShortfalls(next)
      return next
    })
  }, [])

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(18rem,28rem)_1fr]">
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-base font-semibold">{PREP_TEXT.whatToMake}</h2>
        {isLoading ? <p className="text-muted-foreground">{T.loading}</p> : isError ? (
          <LoadError busy={isFetching} onRetry={() => void refetch()} />
        ) : items.length === 0 ? (
          <p className="text-muted-foreground">{PREP_TEXT.nothingToMake}</p>
        ) : (
          <ul className="grid gap-2">
            {items.map((it) => (
              <li key={it.stock_item_id}>
                <button
                  type="button"
                  onClick={() => setPickedId(it.stock_item_id)}
                  className={cn(
                    'flex min-h-10 w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left touch-manipulation',
                    pickedId === it.stock_item_id ? 'border-primary bg-primary/5 ring-2 ring-primary' : 'hover:bg-muted',
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <ChefHat className="h-4 w-4 shrink-0" aria-hidden /> {it.name}
                  </span>
                  <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
                    {PREP_TEXT.onShelf(`${fmtQty(it.kitchen_on_hand)} ${it.unit}`)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {picked && <StartBatch key={picked.stock_item_id} item={picked} run={run} notify={notify} />}
      </section>

      <section className="space-y-4 rounded-lg border bg-card p-4">
        <div>
          <h2 className="mb-2 text-base font-semibold">{PREP_TEXT.openBatches}</h2>
          {open.length === 0 ? (
            <p className="text-muted-foreground">{PREP_TEXT.noOpenBatches}</p>
          ) : (
            <ul className="space-y-3">
              {open.map((b) => <OpenBatchCard key={b.id} batch={b} run={run} notify={notify} onShortfall={setShortfall} />)}
            </ul>
          )}
        </div>
        {recent.length > 0 && (
          <div>
            <h2 className="mb-2 text-base font-semibold">{PREP_TEXT.recentBatches}</h2>
            <ul className="divide-y rounded-lg border">
              {recent.map((b) => (
                <li key={b.id} className="px-3 py-2">
                  <div className="flex justify-between gap-3">
                    <span className="font-medium">{b.prep_item_name}</span>
                    <span className="tabular-nums">
                      {PREP_TEXT.made} {fmtQty(b.actual_yield_qty ?? 0)} / {PREP_TEXT.planned.toLowerCase()} {fmtQty(b.planned_qty)} {b.unit}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                    <span>{b.batch_no}{b.completed_at ? ` · ${formatDateTimeHuman(b.completed_at)}` : ''}{b.produced_by_name ? ` · ${b.produced_by_name}` : ''}</span>
                    <span>{yieldVarianceText(b, fmtQty)}</span>
                  </div>
                  <PlanFor batch={b} />
                  {shortfalls[b.id] && (
                    <div className="mt-1 flex items-start justify-between gap-3 rounded-lg border border-amber-400 bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                      <span>{shortfalls[b.id]}</span>
                      <Button variant="ghost" size="sm" onClick={() => setShortfall(b.id, null)}>{PREP_TEXT.dismiss}</Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  )
}

function StartBatch({ item, run, notify }: { item: KitchenPrepItem; run: KitchenRun; notify: Notify }) {
  const [planned, setPlanned] = useState(String(item.batch_qty))
  const [busy, setBusy] = useState(false)
  const plannedN = Number(planned)
  const needs = useMemo(() => kitchenIngredientsFor(item, plannedN), [item, plannedN])

  const start = async () => {
    setBusy(true)
    try {
      const res = await run<ProductionBatch>(PREP_TEXT.startBatch, 'POST', '/store/production-batches', {
        prep_item_id: item.stock_item_id,
        planned_qty: plannedN,
      })
      if (res.status === 'queued') {
        notify('success', PREP_TEXT.queuedOffline)
        return
      }
      // Mark it cooking. If this one is lost, the batch simply stays
      // "Waiting" — it can still be finished.
      await run(PREP_TEXT.startBatch, 'POST', `/store/production-batches/${res.data.id}/start`, {})
      notify('success', PREP_TEXT.batchStarted(res.data.batch_no))
    } catch (e) {
      notify('error', friendlyError(e, { brief: true }))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <p className="text-sm text-muted-foreground">
        {PREP_TEXT.oneBatch(`${fmtQty(item.batch_qty)} ${item.unit}`)}
        {item.batch_cost ? ` ${PREP_TEXT.batchCost(formatMoney(item.batch_cost))}` : ''}
      </p>
      <label className="block">
        <span className="text-sm font-medium">{PREP_TEXT.howMuchToMake}</span>
        <div className="mt-1 flex items-center gap-2">
          <Input type="number" inputMode="decimal" min={0} step="any" className="w-36 tabular-nums"
            value={planned} onChange={(e) => setPlanned(e.target.value)} />
          <span className="text-base">{item.unit}</span>
        </div>
      </label>
      {needs.length > 0 && (
        <div className="rounded-md bg-muted/50 p-3 text-sm">
          <div className="mb-1 flex items-center justify-between gap-3">
            <p className="text-muted-foreground">{PREP_TEXT.willUse}</p>
            <PrepRecipeButton prepItemId={item.stock_item_id} name={item.name} qty={plannedN > 0 ? plannedN : undefined} />
          </div>
          <ul className="space-y-0.5">
            {needs.map((n) => (
              <li key={n.stock_item_id} className="flex justify-between gap-3">
                <span className="truncate">{n.name}</span>
                <span className="shrink-0 font-medium tabular-nums">{fmtQty(n.qty)} {n.unit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button className="w-full" disabled={!(plannedN > 0) || busy} onClick={() => void start()}>
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{PREP_TEXT.starting}</> : PREP_TEXT.startBatch}
      </Button>
    </div>
  )
}

function OpenBatchCard({ batch, run, notify, onShortfall }: { batch: ProductionBatch; run: KitchenRun; notify: Notify; onShortfall: (batchId: string, text: string | null) => void }) {
  const [made, setMade] = useState(String(batch.planned_qty))
  // F-CTRL-N73: a batch of a tracked prep item needs an expiry date; its
  // shelf life fills one in until somebody types their own.
  const today = businessTodayIso(useDayBoundaryHour())
  const [typedExpiry, setExpiry] = useState<string | null>(null)
  const expiry = typedExpiry ?? defaultExpiryFor(batch, today)
  const [confirmZero, setConfirmZero] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [busy, setBusy] = useState(false)
  const madeN = Number(made)

  const act = async (label: string, path: string, body: unknown, done: string) => {
    setBusy(true)
    try {
      const res = await run<{ shortfalls?: { name: string; unit: string; needed: number; had: number }[] }>(label, 'POST', path, body)
      if (res.status === 'queued') {
        notify('success', PREP_TEXT.queuedOffline)
        return
      }
      const short = res.data?.shortfalls ?? []
      if (short.length > 0) {
        // Saved, but short: the warning stays on the batch (not an error).
        onShortfall(batch.id, `${PREP_TEXT.shortTitle} ${short.map((s) => PREP_TEXT.shortLine(s.name, `${fmtQty(s.needed)} ${s.unit}`, `${fmtQty(s.had)} ${s.unit}`)).join('; ')}. ${PREP_TEXT.shortHelp}`)
      }
      notify('success', done)
    } catch (e) {
      notify('error', friendlyError(e, { brief: true }))
    } finally {
      setBusy(false)
    }
  }

  const finish = () => {
    if (madeN === 0 && !confirmZero) {
      setConfirmZero(true)
      return
    }
    void act(PREP_TEXT.finish, `/store/production-batches/${batch.id}/complete`,
      { actual_yield_qty: madeN, expiry_date: expiry || undefined }, PREP_TEXT.batchFinished(batch.batch_no))
  }

  return (
    <li className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex-1 text-sm font-semibold">{batch.prep_item_name}</span>
        <PrepRecipeButton prepItemId={batch.prep_item_id} name={batch.prep_item_name} qty={batch.planned_qty} />
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-sm">{STATUS_TEXT[batch.status]}</span>
      </div>
      <PlanFor batch={batch} />
      <p className="text-sm text-muted-foreground">
        {batch.batch_no} · {PREP_TEXT.planned}: {fmtQty(batch.planned_qty)} {batch.unit}
        {batch.created_by_name ? ` · ${batch.created_by_name}` : ''}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium">{PREP_TEXT.howMuchMade}</span>
          <div className="mt-1 flex items-center gap-2">
            <Input type="number" inputMode="decimal" min={0} step="any" className="tabular-nums"
              value={made} onChange={(e) => { setMade(e.target.value); setConfirmZero(false) }} />
            <span className="text-base">{batch.unit}</span>
          </div>
        </label>
        <label className="block text-sm">
          <span className={batch.tracks_expiry && madeN > 0 && !expiry ? 'font-medium text-danger-fg' : 'font-medium'}>
            {batch.tracks_expiry ? `${PREP_TEXT.expiryRequiredLabel}*` : PREP_TEXT.expiryLabel}
          </span>
          <Input type="date" className="mt-1" aria-required={batch.tracks_expiry} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </label>
      </div>
      {confirmZero && <p className="text-sm text-warning-fg">{PREP_TEXT.zeroYieldConfirm}</p>}
      {confirmCancel ? (
        <div className="space-y-2 rounded-lg border border-destructive/50 p-3">
          <p className="text-sm font-medium">{PREP_TEXT.cancelBatchConfirm}</p>
          <div className="flex gap-2">
            <Button variant="destructive" disabled={busy}
              onClick={() => void act(PREP_TEXT.cancelBatch, `/store/production-batches/${batch.id}/cancel`, {}, PREP_TEXT.cancelled)}>
              {PREP_TEXT.cancelBatchYes}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setConfirmCancel(false)}>{PREP_TEXT.keepBatch}</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button className="flex-1" disabled={made.trim() === '' || !(madeN >= 0) || (madeN > 0 && expiryMissing(batch, expiry)) || busy} onClick={finish}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{PREP_TEXT.finishing}</> : PREP_TEXT.finish}
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => setConfirmCancel(true)}>
            {PREP_TEXT.cancelBatch}
          </Button>
        </div>
      )}
    </li>
  )
}
